import React, { useRef, useEffect } from 'react';
import { DevicePerformance } from './performance.js';

// ==================== HELPER FUNCTIONS ====================

// Helper: capitalize first letter (UI display only)
export const capitalize = (v) => {
  if (!v || typeof v !== 'string') return v;
  return v.charAt(0).toUpperCase() + v.slice(1);
};

// ==================== KATEGORIE TAG HELPER ====================
export function getCategoryTag(tariff) {
  const title = (tariff.title || '').toLowerCase();
  const group = (tariff.group || '').toLowerCase();
  const category = (tariff.category || '').toLowerCase();
  const hasValidityLeft = tariff.showValidityLeft && (tariff.startsOn || tariff.expiresOn);
  
  // Kombivorteil - KEIN Badge mehr
  if (title.includes('kv') || title.includes('kombi') || group.includes('kombi') || category.includes('kombi') || tariff.isComboDiscounted) {
    return null;
  }
  
  // Blue - links, aber rechts wenn Validity links ist
  if (tariff.segment === 'blue' || category.includes('blue')) {
    return { label: 'BLUE', className: hasValidityLeft ? 'tag-blue tag-right' : 'tag-blue tag-left' };
  }
  // Promo - Badge rechts wenn Validity links
  if (group === 'promo' && hasValidityLeft) {
    return { label: 'PROMO', className: 'tag-promo tag-right' };
  }
  // Young - Badge rechts wenn Validity links
  if (group === 'young' && hasValidityLeft) {
    return { label: 'YOUNG', className: 'tag-young tag-right' };
  }
  return null;
}

// ==================== ICONS ====================

export function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="feature-icon" aria-hidden="true" focusable="false">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="phone-icon" aria-hidden="true" focusable="false">
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <line x1="11" y1="5" x2="13" y2="5" />
    </svg>
  );
}

export function WifiIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="phone-icon" aria-hidden="true" focusable="false">
      <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
      <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
      <circle cx="12" cy="20" r="1"/>
    </svg>
  );
}

// ==================== EFFECTS (Performance-optimiert) ====================

export function ConfettiEffect({ activeEvent }) {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    container.innerHTML = '';
    
    const confettiEvents = ['silvester', 'newyear', 'carnival'];
    if (!confettiEvents.includes(activeEvent)) return;
    if (DevicePerformance.prefersReducedMotion) return;
    
    const baseCount = 40;
    const count = DevicePerformance.getIntensity(baseCount);
    if (count === 0) return;
    
    const colors = activeEvent === 'carnival' 
      ? ['#ff0066', '#00ff66', '#6600ff', '#ffff00', '#ff6600']
      : ['#ffd700', '#ff6b6b', '#4ecdc4', '#ffffff', '#ff69b4'];
    
    const fragment = document.createDocumentFragment();
    
    for (let i = 0; i < count; i++) {
      const piece = document.createElement('div');
      piece.className = 'confetti-piece';
      piece.style.cssText = `
        left: ${Math.random() * 100}%;
        background-color: ${colors[Math.floor(Math.random() * colors.length)]};
        animation-duration: ${5 + Math.random() * 5}s;
        animation-delay: ${Math.random() * 4}s;
        transform: rotate(${Math.random() * 360}deg);
      `;
      fragment.appendChild(piece);
    }
    
    container.appendChild(fragment);
    
    return () => { container.innerHTML = ''; };
  }, [activeEvent]);
  
  return <div ref={containerRef} className="confetti-container" aria-hidden="true" />;
}

export function LeafEffect({ activeEvent }) {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    container.innerHTML = '';
    
    if (activeEvent !== 'autumn') return;
    if (DevicePerformance.prefersReducedMotion) return;
    
    const count = DevicePerformance.getIntensity(20);
    if (count === 0) return;
    
    const leaves = ['🍂', '🍁'];
    const fragment = document.createDocumentFragment();
    
    for (let i = 0; i < count; i++) {
      const leaf = document.createElement('div');
      leaf.className = 'falling-leaf';
      leaf.textContent = leaves[Math.floor(Math.random() * leaves.length)];
      leaf.style.cssText = `
        left: ${Math.random() * 100}%;
        font-size: ${18 + Math.random() * 14}px;
        animation-duration: ${10 + Math.random() * 8}s;
        animation-delay: ${Math.random() * 6}s;
      `;
      fragment.appendChild(leaf);
    }
    
    container.appendChild(fragment);
    
    return () => { container.innerHTML = ''; };
  }, [activeEvent]);
  
  return <div ref={containerRef} className="leaf-container" aria-hidden="true" />;
}

export function EasterEffect({ activeEvent }) {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    container.innerHTML = '';
    
    if (activeEvent !== 'easter') return;
    if (DevicePerformance.prefersReducedMotion) return;
    
    const count = DevicePerformance.getIntensity(15);
    if (count === 0) return;
    
    const items = ['🥚', '🐰', '🌷'];
    const fragment = document.createDocumentFragment();
    
    for (let i = 0; i < count; i++) {
      const item = document.createElement('div');
      item.className = 'easter-item';
      item.textContent = items[Math.floor(Math.random() * items.length)];
      item.style.cssText = `
        left: ${Math.random() * 100}%;
        font-size: ${16 + Math.random() * 12}px;
        animation-duration: ${12 + Math.random() * 6}s;
        animation-delay: ${Math.random() * 5}s;
      `;
      fragment.appendChild(item);
    }
    
    container.appendChild(fragment);
    
    return () => { container.innerHTML = ''; };
  }, [activeEvent]);
  
  return <div ref={containerRef} className="easter-container" aria-hidden="true" />;
}
